import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, Headphones, Paperclip, AlertTriangle } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

const CreateTicket = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const [formData, setFormData] = useState({
    ticketNo: '',
    ticketDate: new Date().toISOString().split('T')[0],
    module: '',
    category: '',
    priority: '',
    severity: '',
    description: '',
    attachments: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, attachments: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Form Data:', formData);
      success('Support ticket raised successfully!');
      setTimeout(() => navigate('/support'), 1500);
    } catch (error) {
      showError('Failed to raise ticket. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/support');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Raise a Ticket"
        description="Submit a new support ticket to our help center team"
        actions={headerActions}
      />

      <div className="page-container">
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Headphones size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Ticket Details
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Ticket No. & Date */}
                  <div>
                    <label style={labelStyle}>Ticket No. *</label>
                    <input
                      type="text"
                      name="ticketNo"
                      value={formData.ticketNo}
                      onChange={handleChange}
                      placeholder="e.g. FGX-XXXXXX"
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Ticket Date</label>
                    <input
                      type="date"
                      name="ticketDate"
                      value={formData.ticketDate}
                      style={{ ...inputStyle, backgroundColor: '#f9fafb' }}
                      readOnly
                    />
                  </div>

                  {/* Module & Category */}
                  <div>
                    <label style={labelStyle}>Module/Functionality *</label>
                    <select
                      name="module"
                      value={formData.module}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    >
                      <option value="">Select Module/Functionality</option>
                      <option value="Overview">Overview</option>
                      <option value="Sourcing">Sourcing</option>
                      <option value="Contracts">Contracts</option>
                      <option value="Projects">Projects</option>
                      <option value="Assets">Assets</option>
                      <option value="Finance">Finance</option>
                      <option value="AI">AI Intelligence</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Category *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    >
                      <option value="">Select Category</option>
                      <option value="Technical Error">Technical Error</option>
                      <option value="Functional Query">Functional Query</option>
                      <option value="Feature Request">Feature Request</option>
                      <option value="Data Issue">Data Issue</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Priority & Severity */}
                  <div>
                    <label style={labelStyle}>Priority *</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Severity *</label>
                    <select
                      name="severity"
                      value={formData.severity}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    >
                      <option value="">Select Severity</option>
                      <option value="S1 - Urgent">S1 - Urgent</option>
                      <option value="S2 - Normal">S2 - Normal</option>
                      <option value="S3 - Minor">S3 - Minor</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Description *</label>
                    <textarea
                      name="description"
                      rows="4"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      placeholder="Enter detailed description of your issue..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                    ></textarea>
                  </div>

                  {/* Attachments */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Attach Documents</label>
                    <div
                      style={{
                        border: '2px dashed #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <input
                        type="file"
                        id="file-upload"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <Paperclip size={20} style={{ color: '#9ca3af', marginBottom: '8px' }} />
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {formData.attachments ? formData.attachments.name : 'Click to upload or drag and drop'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        Maximum file size 10MB
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Save size={16} />
                  {isProcessing ? 'Processing...' : 'Save Ticket'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateTicket;